// frontend/src/features/livestock/compareShots.ts
// Choosing two tank shots to compare (design 7.1), as pure functions over the
// tank's shot index (oldest first): presets resolving to the nearest shot, the
// pair the URL asks for, a pick on one side (same shot blocked, swapped when
// After ends up older), the grid's select mode, and the gap between two shots.
// Any subject's photo index works the same (PhotoIndexItem; livestock compare
// design 8.1); the sentences take the subject's words, tank shots by default.

import type { Dayjs } from 'dayjs';
import type { PhotoIndexItem } from '@/types/photos';
import { TANK_WORDS, type SubjectWords } from './photoSubject';
import { parseServerDate } from './monthGroups';

export type CompareSide = 'before' | 'after';

export interface ComparePair {
  beforeId: number;
  afterId: number;
}

export type PresetKey = '1m' | '3m' | '6m' | '1y' | 'first';

export interface ComparePreset {
  key: PresetKey;
  label: string;
  /** Months before the After shot; null for the first shot. */
  months: number | null;
}

export const COMPARE_PRESETS: ComparePreset[] = [
  { key: '1m', label: '1 month', months: 1 },
  { key: '3m', label: '3 months', months: 3 },
  { key: '6m', label: '6 months', months: 6 },
  { key: '1y', label: '1 year', months: 12 },
  { key: 'first', label: 'First', months: null },
];

/** With fewer than two shots there is nothing to compare: why, for the page and the button. */
export function compareReason(total: number, words: SubjectWords = TANK_WORDS): string | null {
  if (total >= 2) return null;
  return total === 1 ? words.addAnother : words.addSome;
}

function timeOf(item: PhotoIndexItem): number {
  return parseServerDate(item.entry_date).valueOf();
}

/**
 * The shot nearest in time to `target` among `items`; on a tie the older one.
 * Null when there are none.
 */
export function nearestShot(items: PhotoIndexItem[], target: Dayjs | Date): PhotoIndexItem | null {
  const at = target.valueOf();
  let best: PhotoIndexItem | null = null;
  let bestGap = Infinity;
  for (const item of items) {
    const gap = Math.abs(timeOf(item) - at);
    if (gap < bestGap) {
      best = item;
      bestGap = gap;
    }
  }
  return best;
}

/**
 * The Before shot a preset picks: the shot nearest to N months before the After
 * shot, among the shots older than After (so it never picks After itself), or
 * the first shot. Null when After is the oldest shot.
 */
export function resolvePreset(
  items: PhotoIndexItem[],
  preset: ComparePreset,
  afterId: number
): PhotoIndexItem | null {
  const afterAt = items.findIndex((item) => item.id === afterId);
  if (afterAt <= 0) return null;
  const older = items.slice(0, afterAt);
  if (preset.months === null) return older[0] ?? null;
  const after = items[afterAt] as PhotoIndexItem;
  return nearestShot(older, parseServerDate(after.entry_date).subtract(preset.months, 'month'));
}

/**
 * The pair to show for the URL's ids. Missing or unknown ids fall back to First
 * (Before) and Latest (After); the same id on both sides keeps After and takes
 * another shot for Before; an After older than Before is swapped. Null with
 * fewer than two shots.
 */
export function resolvePair(
  items: PhotoIndexItem[],
  beforeId: number | null,
  afterId: number | null
): ComparePair | null {
  return resolvePairDetail(items, beforeId, afterId)?.pair ?? null;
}

export interface ResolvedPair {
  pair: ComparePair;
  /**
   * The sides whose URL id named a shot this tank doesn't have (deleted, or
   * another tank's), so fell back to First or Latest. No id at all isn't one.
   */
  missing: CompareSide[];
}

/** resolvePair, also saying which sides' ids were not found. */
export function resolvePairDetail(
  items: PhotoIndexItem[],
  beforeId: number | null,
  afterId: number | null
): ResolvedPair | null {
  if (items.length < 2) return null;
  const known = (id: number | null): boolean => id === null || items.some((item) => item.id === id);
  const missing: CompareSide[] = [];
  if (!known(beforeId)) missing.push('before');
  if (!known(afterId)) missing.push('after');
  return { pair: pairFor(items, beforeId, afterId), missing };
}

/** The line that says a shot in the URL has gone, and what shows instead. */
export function missingNotice(
  missing: CompareSide[],
  words: SubjectWords = TANK_WORDS
): string | null {
  if (missing.length === 2) {
    return `Those ${words.nounPlural} are no longer here, showing First and Latest`;
  }
  if (missing[0] === 'before') return `That ${words.noun} is no longer here, showing First`;
  if (missing[0] === 'after') return `That ${words.noun} is no longer here, showing Latest`;
  return null;
}

function pairFor(
  items: PhotoIndexItem[],
  beforeId: number | null,
  afterId: number | null
): ComparePair {
  const first = items[0] as PhotoIndexItem;
  const latest = items[items.length - 1] as PhotoIndexItem;
  const position = (id: number | null): number =>
    id === null ? -1 : items.findIndex((item) => item.id === id);

  let afterAt = position(afterId);
  if (afterAt < 0) afterAt = items.length - 1;
  let beforeAt = position(beforeId);
  if (beforeAt < 0) beforeAt = 0;
  if (beforeAt === afterAt) {
    // Both sides on one shot: keep After, and Before is the first shot (or the next one).
    beforeAt = afterAt === 0 ? 1 : 0;
  }
  if (beforeAt > afterAt) [beforeAt, afterAt] = [afterAt, beforeAt];
  // Positions are in range here; the fallbacks only satisfy the type checker.
  return {
    beforeId: items[beforeAt]?.id ?? first.id,
    afterId: items[afterAt]?.id ?? latest.id,
  };
}

export type ChooseOutcome = 'chosen' | 'same' | 'swapped';

export interface ChooseResult {
  pair: ComparePair;
  outcome: ChooseOutcome;
}

/**
 * A shot picked for one side. The shot already on the other side is refused
 * ("Same shot, pick another"); a Before newer than After, or an After older
 * than Before, swaps the two so Before stays the older shot.
 */
export function chooseShot(
  items: PhotoIndexItem[],
  pair: ComparePair,
  side: CompareSide,
  id: number
): ChooseResult {
  const other = side === 'before' ? pair.afterId : pair.beforeId;
  if (id === other) return { pair, outcome: 'same' };
  const next: ComparePair =
    side === 'before' ? { beforeId: id, afterId: pair.afterId } : { beforeId: pair.beforeId, afterId: id };
  const beforeAt = items.findIndex((item) => item.id === next.beforeId);
  const afterAt = items.findIndex((item) => item.id === next.afterId);
  if (beforeAt > afterAt) {
    return { pair: { beforeId: next.afterId, afterId: next.beforeId }, outcome: 'swapped' };
  }
  return { pair: next, outcome: 'chosen' };
}

/**
 * Select mode on the grid: a tap picks a shot, a tap on a picked shot unpicks
 * it, and with two picked a third tap replaces the latest pick.
 */
export function pickShot(picks: number[], id: number): number[] {
  if (picks.includes(id)) return picks.filter((picked) => picked !== id);
  if (picks.length < 2) return [...picks, id];
  return [picks[0] as number, id];
}

/**
 * Two picks as Before and After by date (not by tap order); ties by id, the
 * older upload first. Null until two are picked.
 */
export function orderPicks<T extends { id: number; entry_date: string }>(
  picks: number[],
  shots: T[]
): ComparePair | null {
  if (picks.length < 2) return null;
  const found = picks
    .map((id) => shots.find((shot) => shot.id === id))
    .filter((shot): shot is T => !!shot);
  if (found.length < 2) return null;
  const [a, b] = found as [T, T];
  const aAt = parseServerDate(a.entry_date).valueOf();
  const bAt = parseServerDate(b.entry_date).valueOf();
  const aFirst = aAt < bAt || (aAt === bAt && a.id < b.id);
  return aFirst ? { beforeId: a.id, afterId: b.id } : { beforeId: b.id, afterId: a.id };
}

function plural(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

/** The time between two shots: "Same day", "5 days", "3 weeks", "7 months", "1 year 2 months". */
export function gapLabel(beforeIso: string, afterIso: string): string {
  let from = parseServerDate(beforeIso);
  let to = parseServerDate(afterIso);
  if (to.isBefore(from)) [from, to] = [to, from];
  const days = to.startOf('day').diff(from.startOf('day'), 'day');
  const months = to.diff(from, 'month');
  if (days < 1) return 'Same day';
  if (days < 14) return plural(days, 'day');
  if (months < 2) return plural(Math.floor(days / 7), 'week');
  if (months < 12) return plural(months, 'month');
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return rest ? `${plural(years, 'year')} ${plural(rest, 'month')}` : plural(years, 'year');
}

/** A positive integer id from the URL, or null. */
export function parseShotId(value: string | null): number | null {
  if (!value || !/^\d{1,15}$/.test(value)) return null;
  const id = Number(value);
  return id > 0 ? id : null;
}
